import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  IconButton,
  Typography,
  createTheme,
  ThemeProvider,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { ThreeDots } from "react-loader-spinner";
import CustomTabsV2 from "components/CustomTabs/CustomTabsV2.js";
import InitiativeDetails from "views/InitiativesProgram/initiativeDetails.js";
import OnePage from "views/InitiativesProgram/OnePage_v2.js";
import InitiativeResultsPage from "views/InitiativesProgram/initiativesResults.js";
import InitiativesExecution from "views/InitiativesProgram/initiativesImplementation.js";
import {
  Visibility,
  ViewQuilt,
  AttachMoney,
  Timeline,
} from "@material-ui/icons";
import PropTypes from "prop-types";
import { client } from "../../../../services/axiosClient";
import { useTranslation } from "react-i18next";
import { saveAs } from "file-saver";

// Tema personalizado para garantir que tooltips e modais apareçam acima do Dialog
const tooltipTheme = createTheme({
  overrides: {
    MuiTooltip: {
      popper: {
        zIndex: 10002,
      },
      tooltip: {
        zIndex: 10002,
      },
    },
    // Garantir que dialogs filhos (como risk management) apareçam por cima
    MuiDialog: {
      root: {
        "&.risk-dialog": {
          zIndex: "10100 !important",
        },
      },
      paper: {
        "&.risk-dialog-paper": {
          zIndex: "10100 !important",
        },
      },
    },
    MuiBackdrop: {
      root: {
        "&.risk-dialog-backdrop": {
          zIndex: "10099 !important",
        },
      },
    },
  },
  props: {
    MuiTooltip: {
      disablePortal: false,
      PopperProps: {
        container: () => document.body,
        style: { zIndex: 10002 },
      },
    },
  },
});

const InitiativeDetailsDialog = ({
  isOpen,
  onClose,
  selectedInitiative,
  isLoadingInitiativeDetail,
  clientColor,
  classes,
  t,
  userDetails,
  customStyles = {},
  selectedTabIndex,
  setSelectedTabIndex,
  // KPI states
  initiativeKpisData,
  isKpiLoading,
  kpiError,
  // OnePage states
  currentOnePage,
  isLoadingOnePage,
  onePageError,
}) => {
  const { i18n } = useTranslation();
  const emptyRenderer = () => null;

  // Estado para armazenar múltiplas onePages para histórico
  const [allOnePages, setAllOnePages] = useState(null);
  const [isLoadingAllOnePages, setIsLoadingAllOnePages] = useState(false);

  // Função para buscar todas as onePages da iniciativa
  const fetchAllOnePages = useCallback(async (initiativeId) => {
    if (!initiativeId) return;

    try {
      setIsLoadingAllOnePages(true);
      const response = await client.post("/api/initiative-onepage-data/", {
        action: "list",
        initiative_id: initiativeId,
      });

      if (response.data && response.data.success) {
        const onePages = response.data.data;
        console.log(
          "[DEBUG] InitiativeDetailsDialog: onePages fetched:",
          onePages,
        );

        if (Array.isArray(onePages) && onePages.length > 0) {
          // Ordenar onePages por data (mais recente primeiro)
          const sortedOnePages = onePages.sort(
            (a, b) => new Date(b.date) - new Date(a.date),
          );
          console.log(
            "[DEBUG] InitiativeDetailsDialog: onePages sorted:",
            sortedOnePages,
          );
          setAllOnePages(sortedOnePages);
        } else {
          console.log(
            "[DEBUG] InitiativeDetailsDialog: No onePages data or invalid format",
          );
          setAllOnePages(null);
        }
      } else {
        console.log(
          "[DEBUG] InitiativeDetailsDialog: API error:",
          response.data?.error,
        );
        setAllOnePages(null);
      }
    } catch (error) {
      console.error("Error fetching all onePages:", error);
      setAllOnePages(null);
    } finally {
      setIsLoadingAllOnePages(false);
    }
  }, []);

  // Buscar onePages quando a iniciativa muda
  useEffect(() => {
    if (selectedInitiative?.id && isOpen) {
      console.log(
        "[DEBUG] InitiativeDetailsDialog: Fetching onePages for initiative:",
        selectedInitiative.id,
      );
      fetchAllOnePages(selectedInitiative.id);
    }
  }, [selectedInitiative?.id, isOpen, fetchAllOnePages]);

  // Debug: log when allOnePages changes
  useEffect(() => {
    console.log(
      "[DEBUG] InitiativeDetailsDialog: allOnePages state changed:",
      allOnePages,
    );
  }, [allOnePages]);

  // Secure download function for attachments
  const secureDownload = async (blobName, suggestedName) => {
    try {
      await client.post("/api/awake/");
      const { data } = await client.post(
        "/api/download_initiative_file/",
        { blob_name: blobName },
        { responseType: "blob" },
      );
      saveAs(new Blob([data]), suggestedName);
    } catch (err) {
      console.error("Download error:", err);
    }
  };

  // Implementation states
  const [deliverablesData, setDeliverablesData] = useState(null);
  const [isLoadingDeliverables, setIsLoadingDeliverables] = useState(false);
  const [deliverablesError, setDeliverablesError] = useState(null);

  // Create filtered tabs based on implementation flags - memoized to prevent infinite loops
  const filteredTabs = useMemo(() => {
    const allTabs = [
      {
        key: "one_page",
        tabName: "One Page",
        tabIcon: ViewQuilt,
        alwaysShow: true, // One Page tab always shows as first tab
        flag: "has_op_info",
        tabContent: (
          <div
            style={{
              height: "calc(80vh - 120px)",
              overflowY: "auto",
            }}
          >
            {isLoadingOnePage || isLoadingAllOnePages ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 20,
                }}
              >
                <ThreeDots color={clientColor} height={80} width={80} />
              </div>
            ) : onePageError ? (
              <p style={{ color: "red", padding: "20px" }}>{onePageError}</p>
            ) : allOnePages && allOnePages.length > 0 ? (
              <OnePage
                selectedInitiative={selectedInitiative}
                onePageInfoData={allOnePages}
                defaultOnePageDate={allOnePages[0]?.date} // Use defaultOnePageDate instead of selectedOnePageDate
                setOnePageInfoData={() => {}}
                fetchOnePageData={() => {}}
                onePageHasApproval={{
                  has_approval: userDetails.has_op_approval,
                }}
                t={t}
                classes={classes}
                canReport={false}
                canApprove={false}
                showContent={false}
              />
            ) : currentOnePage ? (
              <OnePage
                selectedInitiative={selectedInitiative}
                onePageInfoData={[currentOnePage]}
                defaultOnePageDate={currentOnePage.date} // Use defaultOnePageDate instead of selectedOnePageDate
                setOnePageInfoData={() => {}}
                fetchOnePageData={() => {}}
                onePageHasApproval={{
                  has_approval: userDetails.has_op_approval,
                }}
                t={t}
                classes={classes}
                canReport={false}
                canApprove={false}
                showContent={false}
              />
            ) : (
              <Typography style={{ padding: "20px", textAlign: "center" }}>
                {t("whatshappening.messages.no_onepage_available")}
              </Typography>
            )}
          </div>
        ),
      },
      {
        key: "implementation",
        tabName: "Implementation",
        tabIcon: Timeline,
        alwaysShow: false,
        flag: "has_deliverables_info",
        tabContent: (
          <div
            style={{
              height: "calc(80vh - 120px)",
              overflowY: "auto",
              padding: "0",
            }}
          >
            {isLoadingDeliverables ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 20,
                }}
              >
                <ThreeDots
                  color={clientColor}
                  height={80}
                  width={80}
                  ariaLabel="loading"
                />
              </div>
            ) : deliverablesError ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <Typography
                  variant="body1"
                  style={{ color: "#d32f2f", textAlign: "center" }}
                >
                  {deliverablesError}
                </Typography>
                <button
                  onClick={fetchDeliverablesData}
                  style={{
                    backgroundColor: clientColor,
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    padding: "8px 16px",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  Tentar Novamente
                </button>
              </div>
            ) : (
              <div style={{ position: "relative", zIndex: 1 }}>
                <InitiativesExecution
                  selectedInitiative={selectedInitiative}
                  deliverablesData={deliverablesData}
                  t={t}
                  classes={classes}
                  clientColor={clientColor}
                  canEdit={false}
                />
              </div>
            )}
          </div>
        ),
      },
      {
        key: "results",
        tabName: "Results",
        tabIcon: AttachMoney,
        alwaysShow: false,
        flag: "has_results_info",
        tabContent: (
          <div
            style={{
              height: "calc(80vh - 120px)",
              overflowY: "auto",
              padding: "1px",
            }}
          >
            {isKpiLoading || isLoadingInitiativeDetail ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 20,
                }}
              >
                <ThreeDots
                  color={clientColor}
                  height={80}
                  width={80}
                  ariaLabel="loading"
                />
              </div>
            ) : kpiError ? (
              <p style={{ color: "red", padding: "20px" }}>{kpiError}</p>
            ) : (
              <InitiativeResultsPage
                selectedInitiative={selectedInitiative}
                t={t}
                classes={classes}
                clientColor={clientColor}
                initiativeKpisData={initiativeKpisData}
                onReloadKpis={null}
                allowKpiEdits={false}
                allowKpiExports={false}
              />
            )}
          </div>
        ),
      },
      {
        key: "details",
        tabName: "Details",
        tabIcon: Visibility,
        alwaysShow: true, // Details tab always shows as last tab
        tabContent: (
          <div
            style={{
              height: "calc(80vh - 120px)",
              overflowY: "auto",
              paddingRight: "10px",
            }}
          >
            <InitiativeDetails
              t={t}
              i18n={i18n}
              classes={classes}
              clientColor={clientColor}
              selectedInitiative={selectedInitiative}
              userDetails={userDetails}
              customStyles={customStyles}
              renderCustomFieldsDetail={emptyRenderer}
              renderKpis={emptyRenderer}
              onDownloadFile={secureDownload}
              showIdentification
              showDescription
              showDates={false}
              showCustomFields={false}
              showKpis={false}
              showClassification={false}
              showEditors={false}
              showAttachments={false}
            />
          </div>
        ),
      },
    ];

    // Filter tabs based on implementation flags
    return allTabs.filter((tab) => {
      if (tab.alwaysShow) return true;
      if (tab.flag && userDetails[tab.flag]) return true;
      return false;
    });
  }, [
    userDetails,
    t,
    classes,
    clientColor,
    selectedInitiative,
    customStyles,
    isLoadingOnePage,
    onePageError,
    currentOnePage,
    allOnePages,
    isLoadingAllOnePages,
    isKpiLoading,
    isLoadingInitiativeDetail,
    kpiError,
    initiativeKpisData,
    isLoadingDeliverables,
    deliverablesError,
  ]);

  // Reset selectedTabIndex if it's out of bounds after filtering
  useEffect(() => {
    if (selectedTabIndex >= filteredTabs.length) {
      setSelectedTabIndex(0);
    }
  }, [filteredTabs.length, selectedTabIndex, setSelectedTabIndex]);

  // Fetch deliverables data when initiative changes or when switching to implementation tab
  useEffect(() => {
    if (selectedInitiative?.id && selectedTabIndex < filteredTabs.length) {
      const currentTab = filteredTabs[selectedTabIndex];
      if (currentTab?.key === "implementation") {
        fetchDeliverablesData();
      }
    }
  }, [selectedInitiative?.id, selectedTabIndex]); // Removed filteredTabs dependency to prevent infinite loop

  const fetchDeliverablesData = async () => {
    if (!selectedInitiative?.id) return;

    setIsLoadingDeliverables(true);
    setDeliverablesError(null);

    try {
      const { data } = await client.post("/api/initiative_deliverables/", {
        action: "list",
        initiative_id: selectedInitiative.id,
      });

      if (data.success) {
        setDeliverablesData(data);
      } else {
        setDeliverablesError(
          data.message || "Erro ao carregar dados de implementação",
        );
      }
    } catch (error) {
      console.error("Erro ao buscar deliverables:", error);
      setDeliverablesError("Erro ao carregar dados de implementação");
    } finally {
      setIsLoadingDeliverables(false);
    }
  };

  return (
    <ThemeProvider theme={tooltipTheme}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth={false}
        // IMPORTANTE: zIndex deve ser maior que o fullScreenContainer (9999)
        // para aparecer corretamente no modo full screen
        style={{ zIndex: 10000 }}
        // Garantir que tooltips apareçam acima do Dialog
        disablePortal={false}
        disableEnforceFocus={true}
        disableAutoFocus={true}
        PaperProps={{
          style: {
            width: "900px",
            height: "80vh",
            margin: "1vh auto",
            borderRadius: "10px",
            overflow: "visible",
            // Garantir que os elementos internos possam ter z-index maior
            position: "relative",
          },
        }}
        BackdropProps={{
          style: { backgroundColor: "#000", opacity: "0.05" }, // Backdrop muito sutil para não interferir com modais filhos
        }}
        // Permitir que tooltips sejam renderizados fora do Dialog
        container={() => document.body}
      >
        {isLoadingInitiativeDetail ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ThreeDots
              color={clientColor}
              height={80}
              width={80}
              ariaLabel="loading"
            />
          </div>
        ) : !selectedInitiative ? (
          <div style={{ flex: 1 }}>
            <p>{t("whatshappening.messages.no_initiatives_selected")}</p>
          </div>
        ) : (
          <div
            style={{
              margin: "-10px -10px 0",
              padding: "0 10px",
              position: "relative",
            }}
          >
            {/* Top-right close button */}
            <IconButton
              aria-label="close dialog"
              onClick={onClose}
              style={{
                position: "absolute",
                top: 26,
                right: 52,
                transform: "none",
                color: clientColor,
                backgroundColor: "#fff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                // Z-index maior que o Dialog para estar sempre visível
                zIndex: 10001,
              }}
              size="small"
            >
              <Close style={{ fontSize: 18 }} />
            </IconButton>

            <CustomTabsV2
              title={t("whatshappening.cards_title.initiatives_details_dialog")}
              headerColor="client"
              value={selectedTabIndex}
              changeValue={(newIndex) => setSelectedTabIndex(newIndex)}
              plainTabs
              tabs={filteredTabs}
              style={{ position: "relative", zIndex: 1 }}
            />
          </div>
        )}
      </Dialog>
    </ThemeProvider>
  );
};

InitiativeDetailsDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedInitiative: PropTypes.object,
  isLoadingInitiativeDetail: PropTypes.bool.isRequired,
  clientColor: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  userDetails: PropTypes.shape({
    has_op_info: PropTypes.bool,
    has_results_info: PropTypes.bool,
    has_deliverables_info: PropTypes.bool,
    has_op_approval: PropTypes.bool,
  }).isRequired,
  customStyles: PropTypes.object,
  selectedTabIndex: PropTypes.number.isRequired,
  setSelectedTabIndex: PropTypes.func.isRequired,
  initiativeKpisData: PropTypes.object,
  isKpiLoading: PropTypes.bool.isRequired,
  kpiError: PropTypes.string,
  currentOnePage: PropTypes.object,
  isLoadingOnePage: PropTypes.bool.isRequired,
  onePageError: PropTypes.string,
};

export default InitiativeDetailsDialog;
